import type {
    Iso8601Date,
    Iso8601DateTime,
    Iso8601Time,
    Uuid,
} from "@vesta/common-type-definitions/typeDefinitions";
import type {FilterConfiguration} from "~/components/ottopilotDataViewer/filterConfiguration";

export type FilterColumnValuesQueryContext = {
    filterConfiguration: FilterConfiguration;
};

export enum OttopilotColumnType {
    string = "92c0cad3-ae65-44ff-b92c-b6020b1f1758",
    uuid = "168ff406-867a-4aab-b742-8d324b63d8fe",
    uuidNullable = "1b72a508-5370-47dc-815f-1e4dc6cfda52",
    number = "4855103c-6ced-4b6c-99ac-0eeea62ef5de",
    boolean = "ee120813-56f1-4153-af08-504faf9065db",
    iso8601DateTime = "1ac7ce08-e298-44e3-b2b1-62f44ae8564a",
    iso8601Date = "03627882-20a7-4a5d-a9df-cf79d2ce35d2",
    iso8601Time = "63f3d96f-134e-4175-87a4-34c295b68c3a",
    array = "d24619df-0f8e-4b08-82bd-f5d01f46a6dc"
}

export function ottopilotColumnTypeToDisplayName(ottopilotColumnType: OttopilotColumnType): string {
    switch (ottopilotColumnType) {
        case OttopilotColumnType.string: {
            return "String";
        }
        case OttopilotColumnType.uuid: {
            return "UUID";
        }
        case OttopilotColumnType.uuidNullable: {
            return "UUID (nullable)";
        }
        case OttopilotColumnType.number: {
            return "Number";
        }
        case OttopilotColumnType.boolean: {
            return "Boolean";
        }
        case OttopilotColumnType.iso8601DateTime: {
            return "DateTime";
        }
        case OttopilotColumnType.iso8601Date: {
            return "Date";
        }
        case OttopilotColumnType.iso8601Time: {
            return "Time";
        }
        case OttopilotColumnType.array: {
            return "Array";
        }
        default: {
            console.error("Invalid ottopilotColumnType", ottopilotColumnType);
            // throw new Error("Invalid ottopilotColumnType");
            return "Unknown";
        }
    }
}

// TODO: Type safety for this thing sucks right now. Fix.
// export type OttopilotColumn<T, U extends keyof OttopilotColumnValueTypeMap> = {
//     // id: Uuid;
//     id: string;
//     type: OttopilotColumnType;
//     displayName: string;
//     valueGetter: (row: T) => OttopilotColumnValueTypeMap[U];
//     valueFormatter?: (value: OttopilotColumnValueTypeMap[U]) => string;
//     // valuesQueryFn: () => Promise<Array<string>>;
//     filteringCapability_contains?: boolean;
//     filteringCapability_in?: {};
//     sortingCapability?: boolean;
// };
// export type OttopilotColumn<T> = {
//     [K in OttopilotColumnType]: {
//         // id: Uuid;
//         id: string;
//         type: OttopilotColumnType;
//         displayName: string;
//         valueGetter: (row: T) => OttopilotColumnValueTypeMap[K];
//         valueFormatter?: (value: OttopilotColumnValueTypeMap[K]) => string;
//         // valuesQueryFn: () => Promise<Array<string>>;
//         filteringCapability_contains?: boolean;
//         filteringCapability_in?: {};
//         sortingCapability?: boolean;
//     };
// }[OttopilotColumnType];

export type OttopilotColumn<T> = {
    // id: Uuid;
    id: string;
    displayName: string;
    // valuesQueryFn: () => Promise<Array<string>>;
    filteringCapability_contains?: boolean;
    filteringCapability_in?: {
        valuesQueryFn?: (context?: FilterColumnValuesQueryContext) => Promise<Array<string>>;
        /** Raw values omitted from the filter picker (e.g. status ids still allowed in applied filters). */
        excludePickerValues?: ReadonlyArray<string>;
    };
    customFilter?: (args: {
        controller: {
            setFilterConfiguration: (
                updater: (prev: FilterConfiguration) => FilterConfiguration,
            ) => void;
        };
        ephemeralValue: {
            revisionId: Uuid;
            lineItems: Array<string> | null;
        };
        column: {
            id: string;
            displayName: string;
        };
        closePopup: () => void;
    }) => void | Promise<void>;
    sortingCapability?: boolean;
    pinned?: 'left' | 'right';
} & {
    [K in OttopilotColumnType]: {
        type: K;
        valueGetter: (row: T) => OttopilotColumnValueTypeMap[K];
        valueFormatter?: (value: OttopilotColumnValueTypeMap[K]) => OttopilotColumnFormatterReturnType<K>;
        cellRenderer?: (row: T) => React.ReactNode;
    };
}[OttopilotColumnType];

type OttopilotColumnFormatterReturnType<K extends OttopilotColumnType> =
    K extends OttopilotColumnType.array ? Array<string> : string;

type OttopilotColumnValueTypeMap = {
    [OttopilotColumnType.string]: string;
    [OttopilotColumnType.uuid]: Uuid;
    [OttopilotColumnType.uuidNullable]: Uuid | null;
    [OttopilotColumnType.number]: number;
    [OttopilotColumnType.boolean]: boolean;
    [OttopilotColumnType.iso8601DateTime]: Iso8601DateTime;
    [OttopilotColumnType.iso8601Date]: Iso8601Date;
    [OttopilotColumnType.iso8601Time]: Iso8601Time;
    [OttopilotColumnType.array]: Array<string>;
};
